import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";

const AdminSubscriptionReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    completedSubscriptions: 0,
    cancelledSubscriptions: 0,
    expiringSoon: 0,
    conversionRate: 0,
    totalRevenue: 0,
    averageRevenue: 0
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all customers
        const customersQuery = query(collection(db, "users"), where("role", "==", "customer"));
        const customersSnap = await getDocs(customersQuery);
        const customersData = customersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCustomers(customersData);

        // Fetch all subscriptions
        const subscriptionsQuery = query(collection(db, "subscriptions"), orderBy("createdAt", "desc"));
        const subscriptionsSnap = await getDocs(subscriptionsQuery);
        const subscriptionsData = subscriptionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSubscriptions(subscriptionsData);

        // Calculate statistics
        const today = new Date();
        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(today.getDate() + 7);

        let totalRevenue = 0;
        let activeCount = 0;
        let completedCount = 0;
        let cancelledCount = 0;
        let expiringSoonCount = 0;

        subscriptionsData.forEach(sub => {
          // Calculate revenue
          totalRevenue += sub.finalPrice || sub.price || 0;

          // Calculate subscription status
          if (sub.status === "active") {
            activeCount++;
            
            // Check if expiring soon
            if (sub.startDate) {
              const startDate = new Date(sub.startDate);
              const endDate = new Date(startDate);
              endDate.setDate(startDate.getDate() + (sub.deliveryDaysPerMonth || 26));
              
              if (endDate <= sevenDaysFromNow && endDate >= today) {
                expiringSoonCount++;
              }
            }
          } else if (sub.status === "completed" || sub.status === "cancelled") {
            if (sub.status === "completed") {
              completedCount++;
            } else {
              cancelledCount++;
            }
          }
        });

        const conversionRate = customersData.length > 0 ? 
          ((activeCount + completedCount + cancelledCount) / customersData.length) * 100 : 0;

        setStats({
          totalCustomers: customersData.length,
          totalSubscriptions: subscriptionsData.length,
          activeSubscriptions: activeCount,
          completedSubscriptions: completedCount,
          cancelledSubscriptions: cancelledCount,
          expiringSoon: expiringSoonCount,
          conversionRate: Math.round(conversionRate * 100) / 100,
          totalRevenue: totalRevenue,
          averageRevenue: subscriptionsData.length > 0 ? Math.round(totalRevenue / subscriptionsData.length) : 0
        });

      } catch (error) {
        console.error("Error fetching subscription reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const exportCSV = () => {
    try {
      const headers = [
        "Customer Name", "Email", "Phone", "Plan Name", "Rotis/Day", 
        "Start Date", "Status", "Price", "Area", "Address"
      ];
      
      const rows = subscriptions.map(sub => [
        sub.userName || sub.name || "N/A",
        sub.userEmail || sub.email || "N/A",
        sub.phone || "N/A",
        sub.planName || "N/A",
        sub.rotisPerDay || sub.rotisPerDay || "N/A",
        formatDate(sub.startDate),
        sub.status || "N/A",
        formatCurrency(sub.finalPrice || sub.price || 0),
        sub.area || "N/A",
        sub.address || sub.addressDetails || "N/A"
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers, ...rows].map(row =>
          row.map(cell => `"${cell}"`).join(",")
        ).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `subscription_reports_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log("📥 CSV exported successfully");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading subscription reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            ← Back to Dashboard
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">Subscription Reports</span>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-green-700">📊 Subscription Reports</h1>
          <button
            onClick={exportCSV}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all"
          >
            📥 Export CSV
          </button>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Total Customers</div>
            <div className="text-3xl font-bold text-green-700">{stats.totalCustomers}</div>
            <div className="text-xs text-gray-500 mt-1">Registered users</div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Active Subscriptions</div>
            <div className="text-3xl font-bold text-green-700">{stats.activeSubscriptions}</div>
            <div className="text-xs text-gray-500 mt-1">Currently active</div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Completed Subscriptions</div>
            <div className="text-3xl font-bold text-blue-700">{stats.completedSubscriptions}</div>
            <div className="text-xs text-gray-500 mt-1">Finished plans</div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Conversion Rate</div>
            <div className="text-3xl font-bold text-purple-700">{stats.conversionRate}%</div>
            <div className="text-xs text-gray-500 mt-1">Customers with subscriptions</div>
          </div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Total Revenue</div>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(stats.totalRevenue)}</div>
            <div className="text-xs text-gray-500 mt-1">All time</div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Average Revenue</div>
            <div className="text-2xl font-bold text-blue-700">{formatCurrency(stats.averageRevenue)}</div>
            <div className="text-xs text-gray-500 mt-1">Per subscription</div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Expiring Soon</div>
            <div className="text-2xl font-bold text-red-700">{stats.expiringSoon}</div>
            <div className="text-xs text-gray-500 mt-1">Within 7 days</div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Cancelled</div>
            <div className="text-2xl font-bold text-yellow-700">{stats.cancelledSubscriptions}</div>
            <div className="text-xs text-gray-500 mt-1">Cancelled plans</div>
          </div>
        </div>

        {/* Recent Subscriptions Table */}
        <div className="bg-white shadow-md rounded-xl overflow-hidden border border-green-100">
          <div className="p-6 border-b border-green-100">
            <h2 className="text-xl font-semibold text-green-700">Recent Subscriptions</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-green-100 text-green-800">
                <tr>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Rotis/Day</th>
                  <th className="px-4 py-3 font-semibold">Start Date</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Price</th>
                  <th className="px-4 py-3 font-semibold">Area</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.slice(0, 10).map((subscription) => (
                  <tr key={subscription.id} className="border-b hover:bg-green-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium">{subscription.userName || subscription.name || "—"}</div>
                        <div className="text-xs text-gray-500">{subscription.userEmail || subscription.email || "—"}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-green-600">{subscription.planName || "—"}</span>
                    </td>
                    <td className="px-4 py-3">{subscription.rotisPerDay || subscription.rotisPerDay || "—"}</td>
                    <td className="px-4 py-3">{formatDate(subscription.startDate)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        subscription.status === "active" ? "bg-green-100 text-green-800" :
                        subscription.status === "completed" ? "bg-blue-100 text-blue-800" :
                        subscription.status === "cancelled" ? "bg-red-100 text-red-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {subscription.status === "active" ? "🔄 Active" :
                         subscription.status === "completed" ? "✅ Completed" :
                         subscription.status === "cancelled" ? "❌ Cancelled" :
                         subscription.status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">{formatCurrency(subscription.finalPrice || subscription.price || 0)}</td>
                    <td className="px-4 py-3">{subscription.area || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/admin/customers/${subscription.userId}`)}
                          className="text-blue-600 hover:text-blue-800 text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded border border-blue-200 transition-all"
                          title="View Customer"
                        >
                          👤
                        </button>
                        {subscription.status === "active" && (
                          <button
                            onClick={() => navigate(`/admin/customers/${subscription.userId}/calendar`)}
                            className="text-green-600 hover:text-green-800 text-xs bg-green-50 hover:bg-green-100 px-2 py-1 rounded border border-green-200 transition-all"
                            title="View Calendar"
                          >
                            📅
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={() => navigate('/admin/customers')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all"
          >
            👥 View All Customers
          </button>
          <button
            onClick={() => navigate('/admin/subscriptions')}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-all"
          >
            📋 Manage Subscriptions
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionReports;




